import { v4 as uuidv4 } from 'uuid';
import * as E from '../shared/Enums';

/**
 * Generates a random ID for use as the DeviceID
 */
export function getRandomId() {
    return uuidv4().replace(/-/g, '')
}


/** Capitalizes the first letter of a string. */
export function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Ensures all fields in the object are present and not null or undefined.
 */
export function validateFields(obj: any, requiredFields: string[]): [boolean, string[]] {
    let isValid = requiredFields.every((field) => field in obj);
    let missingFields = requiredFields.filter((field) => !(field in obj));
    return [isValid, missingFields];
}


/**
 * Helper function to get Eastern Time information for a given date.
 * Returns hour, minute, day of week, and DST status in Eastern Time.
 */
function getEasternTimeInfo(date: Date) {
    const year = date.getUTCFullYear();
    const dayOfWeek = date.getUTCDay(); // 0=Sun, 1=Mon, ..., 6=Sat

    // Find 2nd Sunday in March (DST starts)
    let march = new Date(Date.UTC(year, 2, 1));
    let marchDay = march.getUTCDay();
    let secondSundayMarch = 8 + ((7 - marchDay) % 7);
    let dstStart = new Date(Date.UTC(year, 2, secondSundayMarch, 7, 0, 0)); // 2am EST = 7am UTC

    // Find 1st Sunday in November (DST ends)
    let november = new Date(Date.UTC(year, 10, 1));
    let novemberDay = november.getUTCDay();
    let firstSundayNovember = 1 + ((7 - novemberDay) % 7);
    let dstEnd = new Date(Date.UTC(year, 10, firstSundayNovember, 6, 0, 0)); // 2am EST = 6am UTC

    // Are we in DST?
    let inDst = date >= dstStart && date < dstEnd;
    let offset = inDst ? -4 : -5; // EDT = UTC-4, EST = UTC-5

    // Calculate Eastern time
    let estHour = (date.getUTCHours() + offset + 24) % 24;
    let estMinute = date.getUTCMinutes();

    return { estHour, estMinute, dayOfWeek, inDst, offset };
}

/**
 * Returns true if the current time is within US market hours (9:30am-4:00pm Eastern, Mon-Fri).
 * Automatically accounts for US daylight saving time.
 * Optionally accepts a Date object (defaults to now).
 */
let _lastMarketOpenState: boolean = false;
let _lastCheckedTime: number = 0;
let _hasWarned: boolean = false;

export function isWithinUsMarketHours(date: Date = new Date()): boolean {
    // TODO subscribe to market-open and market-close events rather than constantly check this
    // Only allow force-true in non-production environments
    if (process.env.NODE_ENV !== 'production') {
        if (!_hasWarned) {
        console.warn('[MarketTime] Allowing force-true market open for testing purposes.');
            _hasWarned = true;
        }
        return true;
    }

    const currentTime = Date.now();

    // Check if we should recalculate
    if (currentTime - _lastCheckedTime < 1000) {
        return _lastMarketOpenState;
    }

    const { estHour, estMinute, dayOfWeek } = getEasternTimeInfo(date);

    // Market hours: Monday-Friday, 9:30 AM - 4:00 PM EST/EDT
    let isOpen = true;
    
    // Weekend check
    if (dayOfWeek === 0 || dayOfWeek === 6) isOpen = false;
    
    // Time check: before 9:30 AM or after 4:00 PM
    if (estHour < 9 || (estHour === 9 && estMinute < 30) || estHour >= 16) {
        isOpen = false;
    }

    if (_lastMarketOpenState !== isOpen) {
        if (isOpen) {
            // console.log('[MARKET] Market hours have started. Resuming operations.');
        } else {
            // console.log('[MARKET] Market hours have ended. Pausing operations.');
        }
    }
    
    _lastMarketOpenState = isOpen;
    _lastCheckedTime = currentTime;
    return isOpen;
}

/**
 * Returns the number of milliseconds until the next market open (9:30 AM EST).
 * If market is currently open, returns 0.
 * If it's a weekend, returns time until Monday 9:30 AM.
 */
export function getTimeUntilMarketOpen(date: Date = new Date()): number {
    const { estHour, estMinute, dayOfWeek } = getEasternTimeInfo(date);
    
    // If market is currently open, return 0
    if (isWithinUsMarketHours(date)) {
        return 0;
    }

    // Helper to calculate market open time for a given UTC date
    const getMarketOpenUtc = (utcDate: Date): Date => {
        const year = utcDate.getUTCFullYear();
        
        // Recalculate DST for this specific date
        let march = new Date(Date.UTC(year, 2, 1));
        let marchDay = march.getUTCDay();
        let secondSundayMarch = 8 + ((7 - marchDay) % 7);
        let dstStart = new Date(Date.UTC(year, 2, secondSundayMarch, 7, 0, 0));
        
        let november = new Date(Date.UTC(year, 10, 1));
        let novemberDay = november.getUTCDay();
        let firstSundayNovember = 1 + ((7 - novemberDay) % 7);
        let dstEnd = new Date(Date.UTC(year, 10, firstSundayNovember, 6, 0, 0));
        
        const inDst = utcDate >= dstStart && utcDate < dstEnd;
        
        // 9:30 AM EDT = 1:30 PM UTC (UTC-4)
        // 9:30 AM EST = 2:30 PM UTC (UTC-5)
        const marketOpenUtcHour = inDst ? 13 : 14;
        
        const result = new Date(utcDate);
        result.setUTCHours(marketOpenUtcHour, 30, 0, 0);
        return result;
    };
    
    // Start with today's market open time
    let nextMarketOpen = getMarketOpenUtc(date);
    
    // If that time has passed, or if today is not a market day, move forward
    while (nextMarketOpen <= date || nextMarketOpen.getUTCDay() === 0 || nextMarketOpen.getUTCDay() === 6) {
        nextMarketOpen.setUTCDate(nextMarketOpen.getUTCDate() + 1);
        
        // Skip weekends
        const dow = nextMarketOpen.getUTCDay();
        if (dow === 6) { // Saturday
            nextMarketOpen.setUTCDate(nextMarketOpen.getUTCDate() + 2); // Move to Monday
        } else if (dow === 0) { // Sunday (shouldn't happen but safety)
            nextMarketOpen.setUTCDate(nextMarketOpen.getUTCDate() + 1); // Move to Monday
        }
        
        // Recalculate market open time for the new date (DST may differ)
        nextMarketOpen = getMarketOpenUtc(nextMarketOpen);
    }
    
    return Math.max(0, nextMarketOpen.getTime() - date.getTime());
}



    /**
     * Determines whether an order should be managed by OrderTracker
     * (automatic trigger/conversion logic) or submitted directly to the broker.
     * 
     * Managed orders are those that have a price reference of PERCENT or STOCK, or
     * are of a type that requires special handling (e.g. TRAILING, BRACKET).
     * 
     * @param order The order to evaluate
     * @returns true if OrderTracker should manage this order, false if broker should handle directly
     */
    export function isOrderManaged(
        type: E.OrderType,
        side: E.OrderSide,
        reference: E.PriceReference
    ): boolean {

        // In the new simplified model, FLEX orders are managed, MARKET orders are not
        if (type === E.OrderType.FLEX) {
            return true;
        }
        
        return false;
    }